import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { useResumeSubscription } from '~/hooks/useResumeSubscription';
import { monthlyPrice, unixDate } from '~/utils/pauseFormat';

import styles from './PauseStatusCard.module.css';

function IconPause() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1.2" />
            <rect x="14" y="5" width="4" height="14" rx="1.2" />
        </svg>
    );
}

/**
 * Bloc « Abonnement en pause / Pause programmée » (page Mon compte, écran de
 * résiliation). Avant le début : « Annuler la pause » (rien n'est prélevé ;
 * confirmation si cela rétablit une résiliation programmée). Pendant :
 * « Reprendre maintenant », avec confirmation du prélèvement immédiat.
 * Les boutons ne sont montrés qu'aux admins (les membres voient les dates).
 */
export function PauseStatusCard({
    clientID,
    sub,
    origin,
    onChanged,
}: {
    clientID: string;
    sub: SubscriptionStatus;
    origin: 'account' | 'cancel_flow';
    onChanged: () => void;
}) {
    const { t, i18n } = useTranslation();
    const { resume, resuming, error } = useResumeSubscription(clientID, origin);
    const [confirming, setConfirming] = useState(false);
    const [, setTick] = useState(0);
    const { pause } = sub;
    const startsAtMs = pause?.startsAt ? pause.startsAt * 1000 : null;

    // Re-rendu pile au début de la pause : « Annuler la pause » (sans
    // prélèvement) doit devenir « Reprendre maintenant » (avec confirmation)
    // même si la page reste ouverte. Attente plafonnée à 6 h, puis on revérifie.
    useEffect(() => {
        if (startsAtMs == null || startsAtMs <= Date.now()) return undefined;
        const wait = Math.min(startsAtMs - Date.now() + 500, 6 * 60 * 60 * 1000);
        const timer = setTimeout(() => setTick((n) => n + 1), wait);
        return () => clearTimeout(timer);
    });

    if (!pause || pause.status !== 'paused' || !pause.startsAt) return null;

    const lang = i18n.language;
    const { startsAt } = pause;
    const scheduled = Date.now() / 1000 < startsAt;
    const price = monthlyPrice(sub, lang);
    const canAct = sub.viewerRole !== 'member';
    // Annuler la pause sans confirmation, sauf si ça rétablit une résiliation.
    const needsConfirm = !scheduled || Boolean(pause.restoresCancellation);

    const onConfirmed = async () => {
        // On envoie la phase AFFICHÉE (celle que l'utilisateur a lue et validée) :
        // si la pause a commencé entre-temps, le serveur refuse (phase_mismatch)
        // au lieu de prélever sans confirmation.
        await resume(scheduled ? 'scheduled' : 'active');
        setConfirming(false);
        // Toujours relire : même en cas d'échec, l'état côté Stripe a pu changer.
        onChanged();
    };

    let confirmText = t('account.pause.resumeConfirm', { price });
    let confirmCta = t('account.pause.resumeConfirmCta');
    if (scheduled) {
        confirmText = t('account.pause.cancelPauseConfirm', { date: unixDate(startsAt, lang) });
        confirmCta = t('account.pause.cancelPauseConfirmCta');
    }

    return (
        <div className={styles.pause}>
            <div className={styles.head}>
                <span className={styles.icon}>
                    <IconPause />
                </span>
                <span className={styles.title}>
                    {scheduled ? t('account.pause.titleScheduled') : t('account.pause.titleActive')}
                </span>
                {pause.months ? (
                    <span className={styles.badge}>{t('account.pause.months', { count: pause.months })}</span>
                ) : null}
            </div>

            <dl className={styles.rows}>
                <div className={styles.row}>
                    <dt>{scheduled ? t('account.pause.playUntil') : t('account.pause.pausedSince')}</dt>
                    <dd>{unixDate(startsAt, lang)}</dd>
                </div>
                {pause.resumesAt && (
                    <div className={styles.row}>
                        <dt>{t('account.pause.gamesBack')}</dt>
                        <dd>{unixDate(pause.resumesAt, lang)}</dd>
                    </div>
                )}
                {pause.endsAt && (
                    <div className={styles.row}>
                        <dt>{t('account.pause.billingResumes', { price })}</dt>
                        <dd>{unixDate(pause.endsAt, lang)}</dd>
                    </div>
                )}
            </dl>

            {canAct && confirming && (
                <div className={styles.confirm}>
                    <p>{confirmText}</p>
                    <div className={styles.actions}>
                        <button type="button" className={styles.primary} onClick={onConfirmed} disabled={resuming}>
                            {confirmCta}
                        </button>
                        <button
                            type="button"
                            className={styles.ghost}
                            onClick={() => setConfirming(false)}
                            disabled={resuming}
                        >
                            {t('account.pause.back')}
                        </button>
                    </div>
                </div>
            )}
            {canAct && !confirming && (
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={scheduled ? styles.ghost : styles.primary}
                        onClick={needsConfirm ? () => setConfirming(true) : onConfirmed}
                        disabled={resuming}
                    >
                        {scheduled ? t('account.pause.cancelPause') : t('account.pause.resumeNow')}
                    </button>
                </div>
            )}

            {error && (
                <p className={styles.error} role="alert">
                    {error.message}{' '}
                    {error.portalUrl && (
                        <a href={error.portalUrl} className={styles.link}>
                            {t('account.pause.updateCard')}
                        </a>
                    )}
                </p>
            )}
        </div>
    );
}
